import { type ValueFormatterParams } from "ag-grid-community"
import type { ReactNode } from "react"
import { first, last } from "lodash"
import { round } from "~/utils/math"
import { erf } from "mathjs"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { getNumberFormatLocale } from "~/stores/number-format-store"
import { secondaryMoqLabel } from "~/constants/erpFields"
import { type BudgetType } from "~/models/budget"

export function formatNumberWithSuffix(num: number | null | undefined, digits: number) {
  if (num == null) {
    return ""
  }

  if (Math.abs(num) < 1000) {
    return num.toLocaleString(getNumberFormatLocale(), { maximumFractionDigits: digits })
  }
  if (Math.abs(num) < 10000) {
    // Use Math.abs to also handle negative values
    // return the number separated like so 123456.78 => 123.456,78
    return num.toLocaleString(getNumberFormatLocale(), { maximumFractionDigits: 0 })
  }
  const locale = getNumberFormatLocale()
  if (Math.abs(num) < 1000000) {
    return `${(num / 1000).toLocaleString(locale, { maximumFractionDigits: digits })}k`
  }

  if (Math.abs(num) < 1000000000) {
    return `${(num / 1000000).toLocaleString(locale, { maximumFractionDigits: digits })}m`
  }

  return `${(num / 1000000000).toLocaleString(locale, { maximumFractionDigits: digits })}b`
}

export function formatProductName(
  variantName: string | undefined | null,
  productName: string | undefined | null,
) {
  const variant = variantName ?? ""

  const product = productName ?? ""
  const showBothNames = !variant?.includes(product ?? "")
  return showBothNames ? `${product} / ${variant}` : variant
}

export function stripRedundantColorCode(
  variantName: string | undefined | null,
  variantId: string | undefined | null,
) {
  const name = variantName?.trim()
  if (!name || !variantId) return variantName

  const colorCode = variantId.slice(variantId.lastIndexOf("_") + 1)
  if (!colorCode || colorCode === variantId || !name.startsWith(colorCode)) return variantName

  return name.slice(colorCode.length).replace(/^[\s\-_/]+/, "") || variantName
}

export function formatNumber(num: number | null | undefined, digits: number = 0) {
  if (Number.isNaN(num) || num == null) {
    return ""
  }

  return num.toLocaleString(getNumberFormatLocale(), { maximumFractionDigits: digits })
}

export const formatAmount = (amount: number, unit: string) => `${formatNumber(amount, 0)} ${unit}`

export function formatPercentWithSuffix(num: number | null | undefined) {
  if (num == null) return ""
  if (Math.abs(num) < 1000) return `${formatNumber(num)}%`
  const [scaled, unit] = Math.abs(num) < 1_000_000 ? [num / 1000, "k"] : [num / 1_000_000, "m"]
  return `~${formatNumber(scaled, Math.abs(scaled) < 10 ? 1 : 0)}${unit}%`
}

/**
 * A count with its noun, agreeing in number: `formatCount(1200, "override")` → "1,200 overrides".
 * Pass `plural` for nouns that don't take a trailing "s".
 */
export function formatCount(count: number, singular: string, plural = `${singular}s`) {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

const conjunctionList = new Intl.ListFormat("en", { type: "conjunction" })

/** "A", "A and B", "A, B, and C". */
export const formatList = (items: readonly string[]): string => conjunctionList.format(items)

/**
 * Formats the "Suggested order" cell as `raw (adjusted)`, where `raw` is the raw
 * forecast-driven need and `adjusted` is the MOQ/colli/secondary-MOQ-adjusted order to place.
 * Shows a single number when the two are equal, or when nothing is to be ordered
 * (`adjusted === 0`). Crucially, MOQ fill distributed to zero-demand sizes (raw 0,
 * adjusted > 0) still surfaces as `0 (Y)`, so the per-SKU parentheses reconcile with the
 * variant total instead of silently hiding the allocated remainder.
 */
export function formatSuggestedOrder(rawOrder: number, adjustedOrder: number): string {
  if (rawOrder === adjustedOrder || adjustedOrder === 0) {
    return formatNumber(rawOrder)
  }
  return `${formatNumber(rawOrder)} (${formatNumber(adjustedOrder)})`
}

/** Locale currency placement (`SEK 1 235`). For a trailing code instead, see `suffixedCurrencyFormatters`. */
export function formatCurrency(value: number, currency: string, digits: number = 0): string {
  return new Intl.NumberFormat(getNumberFormatLocale(), {
    style: "currency",
    currency,
    maximumFractionDigits: digits,
  }).format(value)
}

/**
 * Money formatters that append the currency code after the magnitude (`1.2k SEK`), rather than the
 * locale symbol placement `formatCurrency` produces (`SEK 1 235`). `compact` abbreviates, `full`
 * keeps every digit. The code is omitted when no currency is known.
 */
export function suffixedCurrencyFormatters(currency?: string) {
  const withCurrency = (value: string) => `${value}${currency ? ` ${currency}` : ""}`
  return {
    compact: (value: number) => withCurrency(formatNumberWithSuffix(value, 1)),
    full: (value: number) => withCurrency(formatNumber(Math.round(value))),
  }
}

/** A target amount with its unit: `1.234 units`, or `1.234 EUR` for a currency target. */
export function formatBudgetAmount(amount: number, type: BudgetType, currency?: string) {
  return type === "currency"
    ? suffixedCurrencyFormatters(currency).full(amount)
    : formatCount(Math.round(amount), "unit")
}

/**
 * Swaps a hyphen-minus before a digit for U+2212, the design system's minus. Display only: never
 * parse the result.
 */
export function typographicMinus(text: string): string {
  return text.replace(/-(?=\d)/g, "\u2212")
}

/** {@link typographicMinus} for a rendered value: strings are swapped, other nodes pass through. */
export const withTypographicMinus = (node: ReactNode): ReactNode =>
  typeof node === "string" ? typographicMinus(node) : node

/** `+1.234`, `−5,2`, and `0` for anything that rounds to zero, in the company's number format. */
export const formatSignedNumber = (num: number, digits: number = 0): string =>
  typographicMinus(
    num.toLocaleString(getNumberFormatLocale(), {
      maximumFractionDigits: digits,
      signDisplay: "exceptZero",
    }),
  )

export function formatNumberWithSign(num: number | null, digits: number = 0) {
  if (Number.isNaN(num) || num == null) {
    return ""
  }

  const formattedNumber = num.toLocaleString(getNumberFormatLocale(), {
    maximumFractionDigits: digits,
  })
  return num > 0 ? `+${formattedNumber}` : formattedNumber
}

export function formatForecast(num: number | null, digits: number = 1) {
  if (Number.isNaN(num) || num == null) {
    return ""
  }

  const rounded = roundForecast(num)
  return formatNumber(rounded, digits)
}

export function formatDecimalToPercent(num: number, digits: number = 0) {
  if (Number.isNaN(num) || num == null) {
    return "-"
  }
  const value = round(num * 100, digits)
  return `${value.toLocaleString(getNumberFormatLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits })} %`
}

/** Forecasts should only be rounded above a threshold. Sales will always be fully rounded. We might have sales that needs to be rounded when we transform sales data (e.g. weekly to monthly) **/
export function roundForecast(num: number): number {
  if (num < 5) {
    return Math.round(num * 10) / 10
  } else {
    return Math.round(num)
  }
}

/**
 * Session storage is absent under SSR, and Node 25 exposes a method-less stub, so feature-detect
 * `getItem` rather than trusting the object to exist.
 */
export function readSecondaryMoqGroupVariable() {
  return typeof globalThis.sessionStorage?.getItem === "function"
    ? globalThis.sessionStorage.getItem("secondaryMOQ")
    : null
}

export function formatSecondaryMOQName() {
  return secondaryMoqLabel(readSecondaryMoqGroupVariable())
}

export function formatCamelCaseToTitle(str: string) {
  const spaced = str.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function formatSnakeCaseToTitle(str: string) {
  return str
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

export function formatSnakeCaseToCamelCase(str: string) {
  return str
    .toLowerCase()
    .split("_")
    .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join("")
}

export function uniqueValueFormatter(
  params: ValueFormatterParams,
  suffix: string = "values",
  noRange: boolean = false,
) {
  const isArray = Array.isArray(params.value)

  if (isArray && params.value[0] == null) {
    // For unique aggFunc there can be [null]
    return "-"
  }

  if (!isArray || (isArray && params.value?.length === 1)) {
    return params.value
  }

  return formatUniqueValues(params.value, suffix, false, noRange)
}

export function formatUniqueValues(
  values: any[],
  suffix: string = "values",
  unlimitedUniqueValues: boolean = false,
  noRange: boolean = false,
) {
  if (values.length === 0) {
    return "-"
  }

  const maxUniqueValuesLimit = 20

  if (values.length === 1) {
    return values[0]?.toString()
  }

  const isRange =
    !noRange &&
    (unlimitedUniqueValues || values.length <= maxUniqueValuesLimit) &&
    values.every((v: any) => typeof v === "number")

  if (isRange) {
    const sortedValues = values.map((v) => v?.toString()).sort((a: any, b: any) => a - b)
    // E.g. for sizes "S - XL"
    return `${first(sortedValues)} - ${last(sortedValues)}`
  }

  // E.g "10 sizes"
  return `${formatNumber(values.length)} ${suffix}`
}

export function sizeValueFormatter(params: ValueFormatterParams) {
  const isArray = Array.isArray(params.value)

  const hasNoSize =
    params.value == null ||
    params.value === "" ||
    (isArray && params.value.filter((v: unknown) => v != null && v !== "").length === 0)
  if (hasNoSize) return "N/A"

  if (!isArray || (isArray && params.value?.length === 1)) {
    return params.value
  }

  return formatSizeValues(params.value.sort(sortSizesComparator))
}

export function formatSizeValues(values: any[]) {
  const characterLimit = 18
  const maxUniqueValuesLimit = 25

  const sortedValues = values
  const valuesJoined = sortedValues.join(", ")
  if (values?.length <= maxUniqueValuesLimit) {
    return valuesJoined.length > characterLimit
      ? `${first(values)} - ${last(values)}`
      : valuesJoined
  }

  // E.g "10 sizes"
  if (valuesJoined.length > characterLimit || values?.length > maxUniqueValuesLimit) {
    return `${values?.length} sizes`
  }

  // E.g. "S, M, L, XL"
  return valuesJoined
}

// Colors taken from https://flatuicolors.com/palette/es for contrast purposes
// Consider replacing these with colors from tailwinds color library
// If more colors are needed, make sure contrast to other colors are decent
const opacity = 0.5
export const forecastColors = [
  `rgba(52, 172, 224, ${opacity})`,
  `rgba(33, 140, 116, ${opacity})`,
  `rgba(255, 82, 82, ${opacity})`,
  `rgba(255, 121, 63, ${opacity})`,
  `rgba(112, 111, 211, ${opacity})`,
  `rgba(255, 177, 66, ${opacity})`,
  `rgba(132, 129, 122, ${opacity})`,
  `rgba(204, 142, 53, ${opacity})`,
  `rgba(44, 44, 84, ${opacity})`,
  `rgba(51, 217, 178, ${opacity})`,
  `rgba(248, 165, 194, ${opacity})`,
]

export const financeTableColors = {
  sales: `rgb(169, 139, 250)`,
  profit: `rgba(255, 121, 63, ${opacity})`,
  avgProfit: `rgba(255, 82, 82, ${opacity})`,
  avgItemPrice: `rgb(33, 245, 116)`,
  revenue: `rgba(112, 111, 211, ${opacity})`,
  discount: `rgba(255, 193, 7, ${opacity})`,
}

export function getServiceLevelLabel(zfactor: number): string {
  const percentile = (erf(zfactor / Math.sqrt(2)) + 1) / 2
  // using the normal distribution CDF, calculated using the Abramowitz and Stegun formula
  return formatDecimalToPercent(percentile, 0)
}

interface prefixValueConfig {
  isPercentage?: boolean
  decimals?: number
}

export function formatDuration(startTime: Date, endTime: Date | null): string | null {
  if (!endTime) return null
  const seconds = Math.round((endTime.getTime() - startTime.getTime()) / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

export function formatDurationMs(ms: number): string {
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

/** `42s` / `19m` / `2h 11m`: for narrow cells. */
export function formatDurationCompact(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

/** `1 source` / `3 sources`: a count with a regular English plural. */
export function pluralize(n: number, singular: string): string {
  return `${n} ${singular}${n === 1 ? "" : "s"}`
}

export function getDurationColor(startTime: Date, endTime: Date | null): string {
  if (!endTime) return "text-fg-subtle"
  const seconds = (endTime.getTime() - startTime.getTime()) / 1000
  if (seconds > 300) return "text-red-500"
  if (seconds > 60) return "text-orange-500"
  return ""
}

export function prefixNumber(
  value: number,
  config: prefixValueConfig = {
    isPercentage: false,
    decimals: 0,
  },
): string {
  if (Object.is(value, -0)) value = 0
  const isPositive = value > 0
  const { isPercentage, decimals } = config
  if (isPercentage) {
    return isPositive
      ? `+${formatDecimalToPercent(value, decimals)}`
      : formatDecimalToPercent(value, decimals)
  }

  return isPositive ? `+${formatNumber(value, decimals)}` : formatNumber(value, decimals)
}
